import React, { lazy, startTransition, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from './components/Sidebar';
import ImageCarousel from './components/ImageCarousel';
import './styles.css';

// Code-split the archive grid: it isn't needed on first paint and pulls in the
// IntersectionObserver wiring + 96 tiles worth of refs.
const ThumbnailGallery = lazy(() => import('./components/ThumbnailGallery'));

const N_IMAGES = 96;

// Stable key used to look up entries in ImageMetadata.js, which is keyed
// against the legacy `photos/40webp/N.webp` path. Keeping the key shape stable
// avoids touching that 96-entry data file.
const metadataKey = (imageId) => `photos/40webp/${imageId}.webp`;

const shuffle = (array) => {
  const copy = array.slice();
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const App = () => {
  const imageIds = useMemo(
    () => shuffle(Array.from({ length: N_IMAGES }, (_, index) => index + 1)),
    []
  );
  const [activeView, setActiveView] = useState('carousel');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [galleryMounted, setGalleryMounted] = useState(false);

  const currentImageKey = metadataKey(imageIds[selectedIndex]);

  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      setIsDarkMode(mql.matches);
      document.body.classList.toggle('dark-mode', mql.matches);
    };
    apply();
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (activeView === 'gallery') setGalleryMounted(true);
  }, [activeView]);

  const handleSelectFromGallery = useCallback((index) => {
    setSelectedIndex(index);
    setActiveView('carousel');
  }, []);

  const handleViewChange = useCallback((nextView) => {
    startTransition(() => {
      setActiveView(nextView);
    });
  }, []);

  return (
    <div className={`app ${activeView === 'gallery' ? 'archive-fullscreen' : ''}`}>
      <Sidebar
        currentImage={currentImageKey}
        activeView={activeView}
        onViewChange={handleViewChange}
      />
      <div className="content">
        <div className={`view-panel ${activeView === 'carousel' ? 'active' : 'hidden'}`}>
          <ImageCarousel
            imageIds={imageIds}
            initialIndex={selectedIndex}
            onIndexChange={setSelectedIndex}
            isDarkMode={isDarkMode}
          />
        </div>
        <div className={`view-panel ${activeView === 'gallery' ? 'active' : 'hidden'}`}>
          {galleryMounted && (
            <Suspense fallback={null}>
              <header className="archive-header">
                <div className="archive-header-text">
                  <h1>Lex Bolt</h1>
                  <p><i>Shot on film</i></p>
                </div>
                {/* Same two-button toggle the sidebar uses on the Featured
                    page, so switching between views looks identical here. */}
                <div className="view-switch archive-view-switch" role="tablist" aria-label="Layout">
                  <button
                    className={`view-button ${activeView === 'carousel' ? 'active' : ''}`}
                    onClick={() => handleViewChange('carousel')}
                    role="tab"
                    aria-selected={activeView === 'carousel'}
                  >
                    Featured
                  </button>
                  <button
                    className={`view-button ${activeView === 'gallery' ? 'active' : ''}`}
                    onClick={() => handleViewChange('gallery')}
                    role="tab"
                    aria-selected={activeView === 'gallery'}
                  >
                    Archive
                  </button>
                </div>
              </header>
              <ThumbnailGallery
                imageIds={imageIds}
                onSelect={handleSelectFromGallery}
                isActive={activeView === 'gallery'}
              />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
};

export default App;
